import { useDndContext } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { memo, useState } from 'react';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { Heading, Skeleton } from '@treams/treams-ui';

import { tokens } from '../theme/tokens';
import { LibraryCategoryTitle } from './LibraryCategoryTitle';
import { LibraryItem } from './LibraryItem';
import { LibraryMutations } from './LibraryMutations';
import { LibraryRenderer } from './LibraryRenderer';
import { LibraryScorer } from './LibraryScorer';

const Grid = styled(Box)`
	display: grid;
	grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
	gap: ${tokens.gap.group}px ${tokens.gap.list}px;
`;

const Container = styled(Box)<{ dragging?: boolean }>`
	${(props) =>
		props.dragging &&
		`
        background: ${tokens.surface.white};
        border-radius: ${tokens.radius.md}px;
        box-shadow: ${tokens.shadow.md};
        border: 1px solid ${tokens.border.subtle};
    `}
`;

type LibraryCategoryProps<T, U> = {
	active?: boolean;
	query?: string;
	loading?: boolean;
	loadingItems?: string[];
	dragging?: boolean;
	category: T;
	items: U[];
	listeners?: object;
	placeholder?: Partial<U>;
	scorer: LibraryScorer<T, U>;
	renderer: LibraryRenderer<T, U>;
	mutations: LibraryMutations<T, U>;
	onCreateItem?: (item: U) => void;
	onUpdateItem?: (item: U) => void;
	onForgetItem?: (item: U) => void;
	onUpdateCategory(category: T): void;
	onForgetCategory(category: T): void;
};

export function LibraryCategory<
	T extends { id: string; isArchived?: boolean },
	U extends { id: string },
>(props: LibraryCategoryProps<T, U>) {
	const [adding, setAdding] = useState(false);

	return (
		<Container py={3} dragging={props.dragging}>
			{props.renderer.title({
				query: props.query,
				active: props.active,
				loading: props.loading,
				dragging: props.dragging,
				category: props.category,
				listeners: props.listeners,
				onUpdateCategory: props.onUpdateCategory,
				onForgetCategory: props.onForgetCategory,
			})}
			<Grid px={2} pl={4}>
				{props.items
					.filter((item) => props.scorer.item(item) > 0)
					.map((item) =>
						props.dragging || props.category.isArchived || !props.mutations.reorderItem ? (
							<div key={item.id}>
								<LibraryItem
									query={props.query}
									item={item}
									draggable={!props.category.isArchived && !!props.mutations.reorderItem}
									readOnly={props.category.isArchived}
									embeddedHandle={props.renderer.embeddedHandle}
									renderCard={props.renderer.card}
									onForget={props.onForgetItem}
								/>
							</div>
						) : (
							<LibraryItem.Draggable
								key={item.id}
								draggable={!!props.mutations.reorderItem}
								query={props.query}
								loading={props.loadingItems?.includes(item.id)}
								item={item}
								embeddedHandle={props.renderer.embeddedHandle}
								onUpdate={props.onUpdateItem}
								onForget={props.onForgetItem}
								renderCard={props.renderer.card}
							/>
						),
					)}
				{!props.category.isArchived && props.placeholder && adding && (
					<LibraryItem.Draggable
						new
						embeddedHandle={props.renderer.embeddedHandle}
						item={{ category: props.category, ...(props.placeholder as U) }}
						onCancel={() => setAdding(false)}
						onCreate={async (item) => {
							props.onCreateItem?.(item);
							setAdding(false);
						}}
						renderCard={props.renderer.card}
					/>
				)}
			</Grid>
			{!props.category.isArchived && props.placeholder && !adding && (
				<Box px={2} mt={3}>
					<LibraryItem.Placeholder categoryID={props.category.id} onClick={() => setAdding(true)} />
				</Box>
			)}
		</Container>
	);
}

LibraryCategory.Memo = memo(LibraryCategory) as unknown as typeof LibraryCategory;

function LibraryCategoryDraggable<
	T extends { id: string; isArchived: boolean },
	U extends { id: string },
>(props: LibraryCategoryProps<T, U>) {
	const { active } = useDndContext();

	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
		id: `category:${props.category.id}`,
		disabled: (active?.id as string)?.startsWith('item:'),
	});

	return (
		<div
			ref={setNodeRef}
			style={{
				transform: CSS.Translate.toString(transform),
				transition,
				opacity: isDragging ? 0 : 1,
			}}
			{...attributes}
		>
			<LibraryCategory.Memo active={!!active} {...props} listeners={listeners} />
		</div>
	);
}

LibraryCategory.Draggable = memo(
	LibraryCategoryDraggable,
) as unknown as typeof LibraryCategoryDraggable;

function LibraryCategoryDragOverlay<
	T extends { id: string; isArchived: boolean },
	U extends { id: string },
>(props: LibraryCategoryProps<T, U>) {
	const { active } = useDndContext();

	return <LibraryCategory.Memo active={!!active} {...props} />;
}

LibraryCategory.DragOverlay = memo(
	LibraryCategoryDragOverlay,
) as unknown as typeof LibraryCategoryDragOverlay;

function LibraryCategorySkeleton<T, U>(props: { mutations: LibraryMutations<T, U> }) {
	return (
		<Container py={3}>
			<Flex px={3}>
				<Heading.Skeleton mb={3} />
			</Flex>
			<Grid px={2}>
				<Skeleton.Repeat count={4}>
					<LibraryItem.Skeleton draggable={!!props.mutations.reorderItem} />
				</Skeleton.Repeat>
			</Grid>
		</Container>
	);
}

LibraryCategory.Skeleton = LibraryCategorySkeleton;

LibraryCategory.Title = LibraryCategoryTitle;
